import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IS_API_MODE } from "../../shared/config/runtime";
import { listNotices, markNoticeRead } from "../../entities/notice/api";
import { getNoticeItems, normalizeNotice } from "../../entities/notice/normalize.js";
import { getApiErrorMessage } from "../../shared/api/errors";
import logoAsset from "../../assets/tupi-logo.png";

function noticeAvatar() {
  return logoAsset;
}

function formatNoticeTime(value) {
  if (!value) return "刚刚";
  return new Intl.DateTimeFormat("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

export default function NotificationsPage() {
  const queryClient = useQueryClient();
  const noticesQuery = useQuery({
    queryKey: ["notices"],
    queryFn: () => listNotices(),
    enabled: IS_API_MODE,
  });
  const readMutation = useMutation({
    mutationFn: markNoticeRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notices"] }),
  });
  const remoteNotices = useMemo(() => noticesQuery.data || [], [noticesQuery.data]);

  if (IS_API_MODE && noticesQuery.isPending) {
    return <div className="detail-state">正在加载通知……</div>;
  }
  if (IS_API_MODE && noticesQuery.isError) {
    return <div className="detail-state error">{getApiErrorMessage(noticesQuery.error, "通知加载失败")}</div>;
  }

  const items = IS_API_MODE
    ? getNoticeItems(remoteNotices).map((notice) => {
        const normalized = normalizeNotice(notice);
        return {
        id: normalized.id,
        name: normalized.title,
        avatar: noticeAvatar(notice),
        text: normalized.body,
        time: formatNoticeTime(normalized.createdAt),
        unread: !normalized.readAt,
      };
      })
    : [
        ["阿远", logoAsset, "赞了你的帖子", "2 分钟前"],
        ["Momo", logoAsset, "开始关注你", "1 小时前"],
        ["订单通知", logoAsset, "你的需求已通过审核，可以继续支付", "今天 10:24"],
        ["陈屿", logoAsset, "在评论中提到了你", "昨天"],
      ].map(([name, avatar, text, time], index) => ({ id: index, name, avatar, text, time, unread: index < 2 }));

  return (
    <section className="page-container narrow-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">动态</span>
          <h1>通知</h1>
        </div>
      </div>
      <div className="notification-list">
        {items.map((item, index) => (
          <article key={item.id} className={item.unread ? "unread" : ""} onClick={() => IS_API_MODE && item.unread && readMutation.mutate(item.id)}>
            <img className="avatar avatar-md notice-logo-avatar" src={item.avatar} alt="" />
            <div>
              <p><strong>{item.name}</strong> {item.text}</p>
              <small>{item.time}</small>
            </div>
            {!IS_API_MODE && index === 1 ? <button className="primary-button small">关注</button> : null}
          </article>
        ))}
      </div>
    </section>
  );
}
